import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
    } catch (e2) {
      setError(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="max-w-md mx-auto px-6 pt-40 pb-24">
      <img src="/logo.png" alt="Easy Drive Car Rental" className="h-20 w-auto object-contain mb-6" />
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Account recovery</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
        Set a new <span className="italic gold-text">password</span>
      </h1>
      {done ? (
        <div data-testid="reset-success-panel" className="mt-10 bg-card border border-border rounded-2xl p-8 text-center">
          <ShieldCheck size={28} className="gold-text mx-auto" />
          <p className="mt-4 font-ui text-sm text-muted-foreground">Your password has been updated. You can sign in with it now.</p>
          <Link to="/login">
            <Button data-testid="reset-login-button" className="mt-6 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
              Sign In
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-6">
          {!token && <p data-testid="reset-no-token" className="text-sm text-destructive font-ui">Reset token is missing — use the link from your email.</p>}
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">New password (min 8 characters)</Label>
            <Input data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark mt-2" />
          </div>
          <div>
            <Label className="font-ui text-xs tracking-wide text-muted-foreground">Confirm new password</Label>
            <Input data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-dark mt-2" />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-destructive font-ui">{error}</p>}
          <Button data-testid="reset-submit-button" disabled={loading || !token} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-11">
            {loading ? "Updating…" : "Update Password"}
          </Button>
        </form>
      )}
    </div>
  );
}
